import type { ReactiveController, ReactiveControllerHost } from 'lit';
import type { ButtressDbService, CRCallback } from '@buttress/crag';

export class ButtressSubscriptions implements ReactiveController {
  private readonly __getDb: () => ButtressDbService | undefined;

  private __ids: string[] = [];

  constructor(host: ReactiveControllerHost | undefined, getDb: () => ButtressDbService | undefined) {
    this.__getDb = getDb;
    host?.addController(this);
  }

  get active(): boolean {
    return this.__ids.length > 0;
  }

  subscribe(path: string, callback: CRCallback): void {
    const db = this.__getDb();
    if (!db) throw new Error(`Unable to subscribe to ${path} without a db service`);
    this.__ids.push(db.subscribe(path, callback));
  }

  /** Follows one document: `onUpdate` gets the db's copy of it after each change, when it has one. */
  subscribeDoc<T>(collection: string, id: string, onUpdate: (doc: T) => void): void {
    this.subscribe(`${collection}.${id}.*`, () => {
      const doc = this.__getDb()?.get(`${collection}.${id}`) as T | undefined;
      if (doc) onUpdate(doc);
    });
  }

  clear(): void {
    const db = this.__getDb();
    this.__ids.forEach((id) => db?.unsubscribe(id));
    this.__ids = [];
  }

  hostDisconnected(): void {
    this.clear();
  }
}
